import { useEffect, useRef } from 'react'
import { createBadgeScanner, DEFAULT_IDLE_MS } from './badgeScan'

/**
 * Listen for a card being read by a handheld or fixed scanner.
 *
 * The detection lives in `badgeScan.ts`, where it can be tested without a DOM;
 * this is only the wiring — a window listener, an idle timer, and the one
 * decision that needs the event object.
 *
 * ## Why the listener is on `window`
 *
 * A scanner types into whatever has focus, and on a terminal at a machine that
 * is usually nothing at all. Requiring a field to be focused first would mean
 * "click here, then scan", which is one instruction more than the whole feature
 * is worth. Listening globally means a worker walks up, scans, and is in.
 *
 * ## Why the Enter is swallowed and the characters are not
 *
 * The characters land wherever focus is, exactly as a keyboard's would — see
 * `badgeScan.ts` for why trying to suppress them costs real keystrokes from
 * anybody who types quickly. The caller clears the field afterwards.
 *
 * The Enter is different: by the time it arrives the burst is known to be
 * mechanical, and letting it through would submit a half-filled sign-in form.
 * It is swallowed for **any** confirmed scan.
 *
 * ## What the payload means is not decided here
 *
 * The raw text goes to the caller. A KaproLean badge and a worker's existing
 * MES card are both scans and this cannot tell them apart without knowing what
 * the screen intends to do with either — see `badgeTokenFrom` and
 * `employeeCodeFrom` for the two readings, and `Login.tsx` for the choice
 * between them.
 */
export function useScannedInput(
  onScan: (text: string) => void,
  { enabled = true }: { enabled?: boolean } = {},
) {
  /*
   * The callback through a ref, so re-rendering the form does not tear down and
   * rebuild the listener. A listener that is replaced between two keystrokes of
   * a burst loses the buffer, and the scan silently does nothing.
   */
  const handler = useRef(onScan)
  handler.current = onScan

  useEffect(() => {
    if (!enabled) return

    const scanner = createBadgeScanner()
    let idleTimer: ReturnType<typeof setTimeout> | undefined

    const onKeyDown = (event: KeyboardEvent) => {
      // A chord is a person reaching for a shortcut; no wedge sends one.
      if (event.ctrlKey || event.altKey || event.metaKey) return

      const result = scanner.press(event.key, event.timeStamp, event.repeat)

      if (result.type === 'scan') {
        /*
         * Both, and in this order. `preventDefault` stops the form submitting;
         * `stopPropagation` keeps the Enter from reaching a button that happens
         * to be focused, which on this screen is how a scan would otherwise
         * open the camera instead of signing in.
         */
        event.preventDefault()
        event.stopPropagation()
        handler.current(result.text)
      }

      clearTimeout(idleTimer)

      // Armed only while a burst is actually in progress, so an idle login
      // screen is not holding a timer open.
      if (scanner.pending()) {
        idleTimer = setTimeout(() => {
          /*
           * `performance.now()` and `event.timeStamp` are the same clock — both
           * are high-resolution times from the document's time origin — so the
           * gap this compares against is measured in the same units the
           * keystrokes were. Reaching for `Date.now()` here would be off by the
           * age of the page.
           */
          const idle = scanner.idle(performance.now())

          if (idle.type === 'scan') handler.current(idle.text)
        }, DEFAULT_IDLE_MS + 20)
      }
    }

    /*
     * Capture, so the burst is seen before a focused input's own handling of
     * it. Without this the Enter reaches the form first and the page navigates
     * out from under the scan.
     */
    window.addEventListener('keydown', onKeyDown, true)

    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      clearTimeout(idleTimer)
    }
  }, [enabled])
}
